/**
 * @file Defines log levels and utility symbols for logging, along with a reverse lookup object for log levels.
 */
/**
 * Symbol representing a method for custom log serialization.
 *
 * This method is called without arguments.
 */
export const toLog = Symbol.for('@codegrade/logging::toLog');
/** Disabled log level (80) - disables all logging. */
export const LEVEL_DISABLED = 80;
/** Panic log level (70) - critical errors that will crash the application. */
export const LEVEL_PANIC = 70;
/** Fatal log level (60) - critical errors that may crash the application. */
export const LEVEL_FATAL = 60;
/** Error log level (50) - serious issues in the application. */
export const LEVEL_ERROR = 50;
/** Warning log level (40) - non-critical issues. */
export const LEVEL_WARN = 40;
/** Info log level (30) - general operational messages. */
export const LEVEL_INFO = 30;
/** Debug log level (20) - detailed information for debugging. */
export const LEVEL_DEBUG = 20;
/** Trace log level (10) - highly detailed tracing information. */
export const LEVEL_TRACE = 10;
/** Array of all available log levels. */
export const LOGGABLE_LEVELS = [
    LEVEL_PANIC,
    LEVEL_FATAL,
    LEVEL_ERROR,
    LEVEL_WARN,
    LEVEL_INFO,
    LEVEL_DEBUG,
    LEVEL_TRACE,
];
/** Array of all available log levels. */
export const ALL_LEVELS = [LEVEL_DISABLED, ...LOGGABLE_LEVELS];
/** Object mapping log level names to their corresponding numeric values. */
export const REVERSE_LEVEL_LOOKUP = Object.freeze({
    disabled: LEVEL_DISABLED,
    panic: LEVEL_PANIC,
    fatal: LEVEL_FATAL,
    error: LEVEL_ERROR,
    warn: LEVEL_WARN,
    warning: LEVEL_WARN,
    info: LEVEL_INFO,
    debug: LEVEL_DEBUG,
    trace: LEVEL_TRACE,
});
/**
 * Object mapping log level value to their corresponding name.
 *
 * These names should adhere to `[a-zA-Z]`.
 */
export const LEVEL_LOOKUP = Object.freeze({
    [LEVEL_DISABLED]: 'disabled',
    [LEVEL_PANIC]: 'panic',
    [LEVEL_FATAL]: 'fatal',
    [LEVEL_ERROR]: 'error',
    [LEVEL_WARN]: 'warning',
    [LEVEL_INFO]: 'info',
    [LEVEL_DEBUG]: 'debug',
    [LEVEL_TRACE]: 'trace',
});
